import { useParams, useNavigate } from 'react-router-dom';
import { useGetRentalApplication } from '@/hooks/rentalApplication';
import {
  Box,
  Text,
  VStack,
  Spinner,
  Heading,
  Badge,
  HStack,
  Grid,
  GridItem,
  Button,
  Separator,
} from '@chakra-ui/react';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Briefcase,
  Home,
  Info,
} from 'lucide-react';
import { IncomeType } from '@/types/rentalApplication';
import { InfoCard } from '@/components/InfoCard';

const getIncomeTypeLabel = (incomeType: IncomeType): string => {
  const labels: Record<IncomeType, string> = {
    [IncomeType.WORK]: 'Anställd',
    [IncomeType.SELF_EMPLOYED]: 'Egen företagare',
    [IncomeType.UNEMPLOYED]: 'A-kassa',
    [IncomeType.STUDIES]: 'Studier',
    [IncomeType.PENSION]: 'Pension',
    [IncomeType.OWN_COMPANY]: 'Eget företag',
    [IncomeType.ALLOWANCE]: 'Bidrag',
    [IncomeType.OTHER]: 'Övrigt',
  };
  return labels[incomeType] || incomeType;
};

const ApplicationDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: application, isLoading, error } = useGetRentalApplication(id || '');

  if (isLoading) {
    return (
      <Box p={8} display="flex" justifyContent="center" alignItems="center" minH="60vh">
        <Spinner size="xl" colorPalette="blue" />
      </Box>
    );
  }

  if (error || !application) {
    return (
      <Box p={8} maxW="1400px" mx="auto">
        <Button onClick={() => navigate('/admin')} variant="ghost" mb={6} colorPalette="blue">
          <ArrowLeft size={20} />
          Tillbaka till adminpanel
        </Button>
        <InfoCard colorPalette="red">
          <Text color="red.fg" fontWeight="medium">
            Kunde inte ladda ansökan. Kontrollera att ID:t är korrekt eller försök igen senare.
          </Text>
        </InfoCard>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 8 }} maxW="1200px" mx="auto">
      <VStack gap={6} align="stretch">
        {/* Back Button */}
        <Button
          onClick={() => navigate('/admin')}
          variant="ghost"
          colorPalette="blue"
          alignSelf="flex-start"
        >
          <ArrowLeft size={20} />
          Tillbaka till adminpanel
        </Button>

        {/* Header */}
        <Box>
          <Heading size="2xl" mb={2}>
            {application.firstname} {application.lastname}
          </Heading>
          <HStack gap={2}>
            <Badge colorPalette="blue" variant="subtle" size="lg">
              {application.city}
            </Badge>
            <Badge colorPalette={application.smoker ? 'red' : 'green'} variant="subtle" size="lg">
              {application.smoker ? 'Rökare' : 'Icke-rökare'}
            </Badge>
          </HStack>
        </Box>

        {/* Personal Information */}
        <InfoCard
          title={
            <HStack gap={2}>
              <User size={20} />
              <Text>Personuppgifter</Text>
            </HStack>
          }
        >
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            <GridItem>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                  Förnamn
                </Text>
                <Text fontSize="lg">{application.firstname}</Text>
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                  Efternamn
                </Text>
                <Text fontSize="lg">{application.lastname}</Text>
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                  Personnummer
                </Text>
                <Text fontSize="lg">{application.ssn}</Text>
              </VStack>
            </GridItem>
          </Grid>
        </InfoCard>

        {/* Contact Information */}
        <InfoCard
          title={
            <HStack gap={2}>
              <Mail size={20} />
              <Text>Kontaktuppgifter</Text>
            </HStack>
          }
        >
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            <GridItem>
              <VStack align="start" gap={1}>
                <HStack gap={2}>
                  <Mail size={16} color="gray" />
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Email
                  </Text>
                </HStack>
                <Text fontSize="lg" color="blue.fg">
                  <a href={`mailto:${application.email}`}>{application.email}</a>
                </Text>
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1}>
                <HStack gap={2}>
                  <Phone size={16} color="gray" />
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Telefonnummer
                  </Text>
                </HStack>
                <Text fontSize="lg" color="blue.fg">
                  <a href={`tel:${application.phone_number}`}>{application.phone_number}</a>
                </Text>
              </VStack>
            </GridItem>
          </Grid>
        </InfoCard>

        {/* Rental Preferences */}
        <InfoCard
          title={
            <HStack gap={2}>
              <Home size={20} />
              <Text>Bostadspreferenser</Text>
            </HStack>
          }
        >
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            <GridItem>
              <VStack align="start" gap={1}>
                <HStack gap={2}>
                  <MapPin size={16} color="gray" />
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Önskad stad
                  </Text>
                </HStack>
                <Badge colorPalette="blue" size="lg">
                  {application.city}
                </Badge>
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1}>
                <HStack gap={2}>
                  <DollarSign size={16} color="gray" />
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Hyresintervall
                  </Text>
                </HStack>
                <Text fontSize="lg" fontWeight="semibold">
                  {application.rent.min.toLocaleString()} - {application.rent.max.toLocaleString()}{' '}
                  kr/mån
                </Text>
              </VStack>
            </GridItem>
          </Grid>
        </InfoCard>

        {/* Income Information */}
        <InfoCard
          title={
            <HStack gap={2}>
              <Briefcase size={20} />
              <Text>Inkomst och anställning</Text>
            </HStack>
          }
        >
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            <GridItem>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                  Månadsinkomst
                </Text>
                <Text fontSize="2xl" fontWeight="bold" colorPalette="green">
                  {application.income.monthly.toLocaleString()} kr
                </Text>
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1}>
                <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                  Inkomsttyp
                </Text>
                <Badge colorPalette="purple" size="lg">
                  {getIncomeTypeLabel(application.income.income_type)}
                </Badge>
              </VStack>
            </GridItem>
            {application.employer && (
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Arbetsgivare
                  </Text>
                  <Text fontSize="lg">{application.employer}</Text>
                </VStack>
              </GridItem>
            )}
          </Grid>
        </InfoCard>

        {/* Current Landlord Information */}
        {application.current_landlord && (
          <InfoCard
            title={
              <HStack gap={2}>
                <Home size={20} />
                <Text>Nuvarande hyresvärd</Text>
              </HStack>
            }
          >
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
              <GridItem>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Namn
                  </Text>
                  <Text fontSize="lg">{application.current_landlord.name}</Text>
                </VStack>
              </GridItem>
              <GridItem>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Telefonnummer
                  </Text>
                  <Text fontSize="lg" color="blue.fg">
                    <a href={`tel:${application.current_landlord.phone_number}`}>
                      {application.current_landlord.phone_number}
                    </a>
                  </Text>
                </VStack>
              </GridItem>
            </Grid>
          </InfoCard>
        )}

        {/* Additional Information */}
        <InfoCard
          title={
            <HStack gap={2}>
              <Info size={20} />
              <Text>Övrig information</Text>
            </HStack>
          }
        >
          <VStack gap={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
              <GridItem>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Husdjur
                  </Text>
                  <Text fontSize="lg">{application.pets || 'Ingen information angiven'}</Text>
                </VStack>
              </GridItem>
              <GridItem>
                <VStack align="start" gap={1}>
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Barn
                  </Text>
                  <Text fontSize="lg">{application.children || 'Ingen information angiven'}</Text>
                </VStack>
              </GridItem>
            </Grid>

            {application.additional_info && (
              <>
                <Separator />
                <VStack align="start" gap={2}>
                  <Text fontSize="sm" color="fg.muted" fontWeight="medium">
                    Ytterligare information från sökanden
                  </Text>
                  <Box
                    p={4}
                    bg="bg.subtle"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.subtle"
                  >
                    <Text fontSize="md" whiteSpace="pre-wrap">
                      {application.additional_info}
                    </Text>
                  </Box>
                </VStack>
              </>
            )}
          </VStack>
        </InfoCard>

        {/* Action Buttons */}
        <HStack gap={4} justifyContent="flex-end">
          <Button onClick={() => navigate('/admin')} variant="outline" colorPalette="gray">
            Tillbaka
          </Button>
          <a href={`mailto:${application.email}`} style={{ textDecoration: 'none' }}>
            <Button colorPalette="blue">
              <Mail size={20} />
              Kontakta sökanden
            </Button>
          </a>
        </HStack>
      </VStack>
    </Box>
  );
};

export default ApplicationDetailView;
